'use client'

import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckoutPanel } from './checkout-panel'
import type { CheckoutCouponContext } from './checkout-panel'
import type { CheckoutStrategy, CheckoutSuccessOutcome } from './checkout-types'

interface CheckoutDialogProps {
  /** Drives showModal()/close(). Toggling (not unmounting) keeps the TapPay
   *  card-form iframes alive across re-opens. */
  visible: boolean
  /** Hide the dialog. Called on success/dismiss; backdrop + Esc are guarded by
   *  the in-flight state here, so this may be left unguarded by the container. */
  onClose: () => void
  title: string
  subtitle?: string
  dialogTestId: string
  closeTestId: string

  // ── Forwarded to <CheckoutPanel> ───────────────────────────────────────────
  amount: number
  wallet?: { enabled: boolean; total: number } | null
  coupon: CheckoutCouponContext | null
  userEmail: string | null
  strategy: CheckoutStrategy
  note: string
  setup?: React.ReactNode
  subtotalLabel?: React.ReactNode
  summaryNote?: React.ReactNode
  formatAmount?: (amount: number) => string | number
  cardFree?: boolean
  submitLabelOverride?: string
  emailHint?: string
  testIdPrefix: string
  formTestId: string
  resetKey: string
  quoteResetKey?: string
  onSubmittingChange?: (submitting: boolean) => void
  onSuccess?: (tradeId?: number) => void
  onSuccessOutcome?: (outcome: CheckoutSuccessOutcome) => void
  onRequiresSignIn?: (email: string, paid: boolean) => void
  onCardRequired?: () => void
  redirect?: (url: string) => void
  skipScript?: boolean
  /** Authed purchases: enable the 發票 toggle + fields in the order summary
   *  (load on open, save before charge). Forwarded to <CheckoutPanel>. */
  invoiceLoadFn?: typeof import('../api-client').fetchInvoiceSettings
  invoiceSaveFn?: typeof import('../api-client').saveInvoiceSettings
}

/**
 * The shared `.kiao-modal` checkout dialog: the `<dialog>` chrome (head, close,
 * showModal/close, submitting-guarded dismiss) wrapping a `<CheckoutPanel>`
 * body. Deliberately a native `<dialog>` (not the ui-kit KiaoModal, which
 * unmounts its children on close and would destroy the TapPay-hosted iframes).
 */
export function CheckoutDialog({
  visible,
  onClose,
  title,
  subtitle,
  dialogTestId,
  closeTestId,
  onSubmittingChange,
  ...panelProps
}: CheckoutDialogProps) {
  const { t } = useTranslation('web')
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const [submitting, setSubmitting] = useState(false)
  const handleSubmittingChange = useCallback(
    (nextSubmitting: boolean) => {
      setSubmitting(nextSubmitting)
      onSubmittingChange?.(nextSubmitting)
    },
    [onSubmittingChange]
  )

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (visible && !dialog.open) {
      // jsdom lacks showModal() — fall back to the open attribute.
      if (typeof dialog.showModal === 'function') dialog.showModal()
      else dialog.setAttribute('open', '')
    } else if (!visible && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
    }
  }, [visible])

  useEffect(() => {
    if (!visible) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [visible])

  // Backdrop click + Esc dismiss the dialog, but never mid-charge.
  const guardedClose = () => {
    if (!submitting) onClose()
  }

  return (
    <dialog
      ref={dialogRef}
      // Any TWO-COLUMN checkout gets the wider shell — a purchase (coupon
      // summary) OR a wallet top-up (no coupon, but it collects an invoice). Only
      // bind-card (amount 0, no invoice) stays the default single-column modal.
      // Must mirror <CheckoutPanel>'s `hasSummary` so the shell width matches the
      // number of columns it renders.
      className={`kiao-modal${
        panelProps.coupon !== null ||
        (panelProps.amount > 0 && Boolean(panelProps.invoiceLoadFn && panelProps.invoiceSaveFn))
          ? ' kiao-modal--checkout'
          : ''
      }`}
      aria-labelledby={titleId}
      data-testid={dialogTestId}
      onCancel={(event) => {
        event.preventDefault()
        guardedClose()
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) guardedClose()
      }}
    >
      <div className="km-head">
        <div>
          <h3 id={titleId}>{title}</h3>
          {subtitle ? <p className="km-sub">{subtitle}</p> : null}
        </div>
        <button
          type="button"
          className="km-close"
          aria-label={t('billing.modalCloseAria')}
          disabled={submitting}
          data-testid={closeTestId}
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <CheckoutPanel
        {...panelProps}
        onClose={onClose}
        onSubmittingChange={handleSubmittingChange}
      />
    </dialog>
  )
}
